/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Phone, X, Settings, History, Calculator as KeypadIcon, ChevronDown, ChevronUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Keypad } from './components/Keypad';
import { StatusBlock } from './components/StatusBlock';
import { SettingsBlock } from './components/SettingsBlock';
import { CallHistory } from './components/CallHistory';
import type { SoftPhoneProps, SoftPhoneState } from './types';

// Using global SIP context
import { useSipContext } from '@/context/SipContext';
import { SipCallManager } from '@/components/sip/SipCallManager';
import { softPhoneState } from '@/constants/soft-phone-state';
import { makeCall } from './call-helpers';

export function SoftPhone({
  setConnectOnStartToLocalStorage,
  connectOnStart,
  setNotifications,
  notifications,
  setCallVolume,
  setRingVolume,
  softPhoneOpen,
  setSoftPhoneOpen,
  callVolume,
  ringVolume,
  builtInLauncher = false,
  launcherPosition = 'bottom-right',
  launcherSize = 'medium',
  launcherColor = 'default',
  oldCallHistory = [],
  onCallNumber,
  onHistoryTabClick,
  recording_url,
  apiKey,
}: SoftPhoneProps) {
  // Using global SIP context only
  const globalSipContext = useSipContext();

  const player = useRef<HTMLAudioElement>(null);
  const ringer = useRef<HTMLAudioElement>(null);

  const [launcherOpen, setLauncherOpen] = useState(false);
  const [dialState, setDialState] = useState('');
  const [activeChannel] = useState(0);
  const [notificationState, setNotificationState] = useState({ open: false, message: '' });
  const [calls] = useState<any[]>([]);
  const [showKeypad, setShowKeypad] = useState(false);

  const defaultSoftPhoneState: SoftPhoneState = {
    ...softPhoneState,
    connectOnStart,
    notifications,
    callVolume: typeof callVolume === 'number' && !isNaN(callVolume) ? callVolume : 0.5,
    ringVolume: typeof ringVolume === 'number' && !isNaN(ringVolume) ? ringVolume : 0.5,
  };

  const [localStatePhone, setLocalStatePhone] = useState<SoftPhoneState>(defaultSoftPhoneState);

  const notify = useCallback((message: string) => {
    if (message) {
      setNotificationState({ open: true, message });
      const timeout = setTimeout(() => {
        setNotificationState(prev => ({ ...prev, open: false }));
      }, 3000);

      return () => {
        clearTimeout(timeout)
      }
    }
  }, []);

  // All event handling is now managed by the global SIP context
  // No need for local event handlers

  // Initialize audio elements only (SIP initialization handled by global context)
  useEffect(() => {
    if (player.current) {
      player.current.defaultMuted = false;
      player.current.autoplay = true;
      player.current.volume = Math.max(0, Math.min(1, localStatePhone.callVolume));
    }

    if (ringer.current) {
      ringer.current.src = '/sound/ringtone.ogg';
      ringer.current.loop = true;
      ringer.current.volume = Math.max(0, Math.min(1, localStatePhone.ringVolume));
    }
  }, [localStatePhone.callVolume, localStatePhone.ringVolume]);

  // Request notification permission when softphone opens
  useEffect(() => {
    if (softPhoneOpen && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then((permission) => {
        console.log('Notification permission:', permission);
      });
    }
  }, [softPhoneOpen]);

  const handleConnectPhone = useCallback((_event: React.MouseEvent | null, connectionStatus: boolean) => {
    // Connection is now handled by global SIP context
    if (connectionStatus) {
      globalSipContext.connect?.();
    } else {
      globalSipContext.disconnect?.();
    }
  }, [globalSipContext]);

  const handleSettingsSlider = useCallback((name: string, newValue: number) => {
    const safeValue = Math.max(0, Math.min(1, newValue));

    setLocalStatePhone(prev => ({ ...prev, [name]: safeValue }));

    switch (name) {
      case 'ringVolume':
        if (ringer.current) {
          ringer.current.volume = safeValue;
        }
        setRingVolume(safeValue);
        break;
      case 'callVolume':
        if (player.current) {
          player.current.volume = safeValue;
        }
        setCallVolume(safeValue);
        break;
    }
  }, [setRingVolume, setCallVolume]);

  const handleCall = useCallback(async () => {
    makeCall(dialState, globalSipContext, notify)
  }, [dialState, globalSipContext, notify]);

  const handleEndCall = useCallback(() => {
    // Call end is handled by global SIP context through SipCallManager
    globalSipContext.endCall?.();
  }, [globalSipContext]);

  const handlePressKey = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const digit = event.currentTarget.value;
    setDialState(prev => prev + digit);
  }, []);

  const handleMicMute = useCallback(() => {
    globalSipContext.toggleMute?.();
  }, [globalSipContext]);

  const handleHold = useCallback((sessionId: string, hold: boolean) => {
    if (!hold) {
      globalSipContext.holdCall?.(sessionId);
    } else {
      globalSipContext.unholdCall?.(sessionId);
    }
  }, [globalSipContext]);

  const handleConnectOnStart = useCallback((_event: React.ChangeEvent<HTMLInputElement>, newValue: boolean) => {
    setLocalStatePhone(prev => ({ ...prev, connectOnStart: newValue }));
    setConnectOnStartToLocalStorage(newValue);
  }, [setConnectOnStartToLocalStorage]);

  const handleNotifications = useCallback((_event: React.ChangeEvent<HTMLInputElement>, newValue: boolean) => {
    setLocalStatePhone(prev => ({ ...prev, notifications: newValue }));
    setNotifications(newValue);
  }, [setNotifications]);

  const handleDialNumberOnEnter = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleCall();
    }
  }, [handleCall]);

  const isOpen = builtInLauncher ? launcherOpen : softPhoneOpen;

  const launcherPositionClasses = {
    'bottom-right': 'fixed bottom-4 right-4',
    'bottom-left': 'fixed bottom-4 left-4',
    'top-right': 'fixed top-4 right-4',
    'top-left': 'fixed top-4 left-4'
  };

  const launcherSizeClasses = {
    small: 'h-10 w-10',
    medium: 'h-14 w-14',
    large: 'h-16 w-16'
  };

  // Render inline card when not using built-in launcher
  if (!builtInLauncher && softPhoneOpen) {
    return (
      <Card className="w-80 sm:w-96 shadow-lg bg-background relative z-10">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <Phone className="h-5 w-5" />
              <span>Softphone</span>
            </CardTitle>
            <Button
              onClick={() => setSoftPhoneOpen(false)}
              variant="ghost"
              size="sm"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Notification Alert */}
          {notificationState.open && (
            <Alert className="border-orange-200 bg-orange-50">
              <AlertDescription className="text-orange-800">
                {notificationState.message}
              </AlertDescription>
            </Alert>
          )}

          {/* Call Queue - Now handled by SipCallManager */}

          {/* Inline Call Manager */}
          {isOpen && <SipCallManager inline={true} />} 

          {/* Main Content */}
          <Tabs defaultValue="keypad" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="keypad" className="text-xs">
                <KeypadIcon className="h-4 w-4 mr-1" />
                Keypad
              </TabsTrigger>
              <TabsTrigger value="settings" className="text-xs">
                <Settings className="h-4 w-4 mr-1" />
                Settings
              </TabsTrigger>
              <TabsTrigger value="history" className="text-xs" onClick={() => onHistoryTabClick?.()}>
                <History className="h-4 w-4 mr-1" />
                History
              </TabsTrigger>
            </TabsList>

            <TabsContent value="keypad" className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <Badge variant={localStatePhone.displayCalls[activeChannel].inCall ? "default" : "secondary"}>
                      {localStatePhone.displayCalls[activeChannel].inCall ? "In Call" : "Ready"}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowKeypad(!showKeypad)}
                      className="h-8"
                    >
                      {showKeypad ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      <span className="ml-1 text-xs">{showKeypad ? "Hide" : "Show"} Keypad</span>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    value={dialState}
                    onChange={(e) => setDialState(e.target.value)}
                    onKeyUp={handleDialNumberOnEnter}
                    placeholder="Enter phone number"
                    className="text-center text-lg font-mono"
                  />

                  {showKeypad && (
                    <Keypad
                      handleCall={handleCall}
                      handleEndCall={handleEndCall}
                      handlePressKey={handlePressKey}
                      handleMicMute={handleMicMute}
                      handleHold={handleHold}
                      activeChannel={localStatePhone.displayCalls[activeChannel]}
                      dialState={dialState}
                      setDialState={setDialState}
                    />
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="settings">
              <SettingsBlock
                localStatePhone={localStatePhone}
                handleConnectPhone={handleConnectPhone}
                handleSettingsSlider={handleSettingsSlider}
                handleConnectOnStart={handleConnectOnStart}
                handleNotifications={handleNotifications}
                callVolume={callVolume}
              />
            </TabsContent>

            <TabsContent value="history">
              <CallHistory
                calls={calls}
                oldCallHistory={oldCallHistory}
                onCallNumber={onCallNumber}
                recording_url={recording_url}
                apiKey={apiKey}
              />
            </TabsContent>
          </Tabs>

          {/* Status */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 p-2">
                {globalSipContext.status.isConnected && globalSipContext.status.isRegistered ? (
                  <div className="flex items-center gap-1 text-green-600">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <Phone className="h-4 w-4" />
                    <span className="text-xs font-medium">SIP Connected</span>
                  </div>
                ) : globalSipContext.status.isConnecting ? (
                  <div className="flex items-center gap-1 text-yellow-600">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                    <Phone className="h-4 w-4" />
                    <span className="text-xs font-medium">Connecting...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-red-600">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <Phone className="h-4 w-4" />
                    <span className="text-xs font-medium">SIP Disconnected</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1">
              <StatusBlock
                connectedPhone={globalSipContext.status.isConnected && globalSipContext.status.isRegistered}
                connectingPhone={globalSipContext.status.isConnecting}
              />
            </div>
          </div>
        </CardContent>

        {/* Hidden Audio Elements */}
        <div className="hidden">
          <audio ref={player} preload="auto" />
          <audio ref={ringer} preload="auto" />
        </div>
      </Card>
    );
  }

  return (
    <div>
      {/* Built-in Launcher Button */}
      {builtInLauncher && (
        <Button
          onClick={() => {
            setLauncherOpen(!launcherOpen);
            setSoftPhoneOpen(!launcherOpen);
          }}
          className={`${launcherPositionClasses[launcherPosition]} ${launcherSizeClasses[launcherSize]} rounded-full z-50 shadow-lg`}
          variant={launcherColor}
        >
          {isOpen ? <X className="h-6 w-6" /> : <Phone className="h-6 w-6" />}
        </Button>
      )}

      {/* Softphone Sheet */}
      <Sheet open={isOpen} onOpenChange={builtInLauncher ? setLauncherOpen : setSoftPhoneOpen}>
        <SheetContent side="right" className="w-[420px] sm:w-[480px] overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center space-x-2">
                <Phone className="h-5 w-5" />
                <span>Softphone</span>
              </SheetTitle>
            </div>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            {/* Notification Alert */}
            {notificationState.open && (
              <Alert className="border-orange-200 bg-orange-50">
                <AlertDescription className="text-orange-800">
                  {notificationState.message}
                </AlertDescription>
              </Alert>
            )}

            {/* Call Queue - Now handled by SipCallManager */}

            {/* Inline Call Manager */}
            <SipCallManager inline={true} />

            {/* Main Content */}
            <Tabs defaultValue="keypad" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="keypad" className="text-xs">
                  <KeypadIcon className="h-4 w-4 mr-1" />
                  Keypad
                </TabsTrigger>
                <TabsTrigger value="settings" className="text-xs">
                  <Settings className="h-4 w-4 mr-1" />
                  Settings
                </TabsTrigger>
                <TabsTrigger value="history" className="text-xs" onClick={() => onHistoryTabClick?.()}>
                  <History className="h-4 w-4 mr-1" />
                  History
                </TabsTrigger>
              </TabsList>

              <TabsContent value="keypad" className="space-y-4">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <Badge variant={localStatePhone.displayCalls[activeChannel].inCall ? "default" : "secondary"}>
                        {localStatePhone.displayCalls[activeChannel].inCall ? "In Call" : "Ready"}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowKeypad(!showKeypad)}
                        className="h-8"
                      >
                        {showKeypad ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        <span className="ml-1 text-xs">{showKeypad ? "Hide" : "Show"} Keypad</span>
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Input
                      value={dialState}
                      onChange={(e) => setDialState(e.target.value)}
                      onKeyUp={handleDialNumberOnEnter}
                      placeholder="Enter phone number"
                      className="text-center text-lg font-mono"
                    />

                    {showKeypad && (
                      <Keypad
                        handleCall={handleCall}
                        handleEndCall={handleEndCall}
                        handlePressKey={handlePressKey}
                        handleMicMute={handleMicMute}
                        handleHold={handleHold}
                        activeChannel={localStatePhone.displayCalls[activeChannel]}
                        dialState={dialState}
                        setDialState={setDialState}
                      />
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="settings">
                <SettingsBlock
                  localStatePhone={localStatePhone}
                  handleConnectPhone={handleConnectPhone}
                  handleSettingsSlider={handleSettingsSlider}
                  handleConnectOnStart={handleConnectOnStart}
                  handleNotifications={handleNotifications}
                  callVolume={callVolume}
                />
              </TabsContent>

              <TabsContent value="history">
                <CallHistory
                  calls={calls}
                  oldCallHistory={oldCallHistory}
                  onCallNumber={onCallNumber}
                  recording_url={recording_url}
                  apiKey={apiKey}
                />
              </TabsContent>
            </Tabs>

            {/* Status */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 p-2 rounded">
                  {globalSipContext.status.isConnected && globalSipContext.status.isRegistered ? (
                    <div className="flex items-center gap-1 text-green-600">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <Phone className="h-4 w-4" />
                      <span className="text-xs font-medium">SIP Connected</span>
                    </div>
                  ) : globalSipContext.status.isConnecting ? (
                    <div className="flex items-center gap-1 text-yellow-600">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                      <Phone className="h-4 w-4" />
                      <span className="text-xs font-medium">Connecting...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-red-600">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <Phone className="h-4 w-4" />
                      <span className="text-xs font-medium">SIP Disconnected</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1">
                <StatusBlock
                  connectedPhone={globalSipContext.status.isConnected && globalSipContext.status.isRegistered}
                  connectingPhone={globalSipContext.status.isConnecting}
                />
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Hidden Audio Elements */}
      <div className="hidden">
        <audio ref={player} preload="auto" />
        <audio ref={ringer} preload="auto" />
      </div>
    </div>
  );
}